<script setup>
defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["view", "follow-up"]);

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const agingText = (value) => {
  const map = {
    "1_30": "1 - 30 วัน",
    "31_60": "31 - 60 วัน",
    "61_90": "61 - 90 วัน",
    "90_plus": "90+ วัน",
  };
  return map[value] || value;
};

const agingBadgeClass = (value) => {
  const map = {
    "1_30": "text-bg-warning",
    "31_60": "text-bg-danger",
    "61_90": "text-bg-danger",
    "90_plus": "text-bg-dark",
  };
  return map[value] || "text-bg-light";
};

const contactText = (value) => {
  const map = {
    new: "ยังไม่ติดตาม",
    contacted: "ติดตามแล้ว",
    promise_to_pay: "รับปากจะชำระ",
    escalated: "ส่งต่อผู้บริหาร",
    legal_review: "อยู่ระหว่างกฎหมาย",
  };
  return map[value] || value;
};

const contactBadgeClass = (value) => {
  const map = {
    new: "text-bg-secondary",
    contacted: "text-bg-primary",
    promise_to_pay: "text-bg-info",
    escalated: "text-bg-warning",
    legal_review: "text-bg-dark",
  };
  return map[value] || "text-bg-light";
};

const escapeCSV = (value) => {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
};

const exportCSV = (items) => {
  if (!items.length) return;

  const headers = [
    "เลขที่บิล",
    "อาคาร",
    "ลูกบ้าน",
    "หมายเหตุ",
    "ยูนิต",
    "รอบบิล",
    "ครบกำหนด",
    "อายุหนี้",
    "จำนวนวันค้าง",
    "ยอดค้าง",
    "ค่าปรับ",
    "สถานะติดตาม",
    "วันที่เตือนล่าสุด",
  ];

  const rows = items.map((item) => [
    item.invoiceNo,
    item.building,
    item.residentName,
    item.note,
    item.unitCode,
    item.billingMonth,
    formatDate(item.dueDate),
    agingText(item.agingGroup),
    item.overdueDays || 0,
    item.totalOutstanding || 0,
    item.penaltyAmount || 0,
    contactText(item.contactStatus),
    item.lastReminderDate ? formatDate(item.lastReminderDate) : "-",
  ]);

  const csvContent = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.setAttribute("download", `overdue-items-${today}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>

<template>
  <div class="card border-0 shadow-sm rounded-4">
    <div class="card-body p-0">
      <div
        class="px-4 pt-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div>
          <h5 class="fw-bold mb-1">รายการค้างชำระ</h5>
          <div class="text-muted small">
            พบทั้งหมด {{ items.length }} รายการ
          </div>
        </div>

        <button
          type="button"
          class="btn btn-outline-danger border-danger-subtle rounded-3 shadow-sm"
          :disabled="!items.length"
          @click="exportCSV(items)"
        >
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">เลขที่บิล</th>
              <th>ลูกบ้าน</th>
              <th>ยูนิต</th>
              <th>รอบบิล</th>
              <th>ครบกำหนด</th>
              <th>อายุหนี้</th>
              <th class="text-end">ยอดค้าง</th>
              <th>สถานะติดตาม</th>
              <th class="text-center pe-4">จัดการ</th>
            </tr>
          </thead>

          <tbody v-if="items.length">
            <tr v-for="item in items" :key="item.id">
              <td class="ps-4">
                <div class="fw-semibold">{{ item.invoiceNo }}</div>
                <div class="small text-muted">{{ item.building }}</div>
              </td>
              <td>
                <div class="fw-semibold">{{ item.residentName }}</div>
                <div class="small text-muted">{{ item.note }}</div>
              </td>
              <td>{{ item.unitCode }}</td>
              <td>{{ item.billingMonth }}</td>
              <td>{{ formatDate(item.dueDate) }}</td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="agingBadgeClass(item.agingGroup)"
                >
                  {{ agingText(item.agingGroup) }}
                </span>
                <div class="small text-muted mt-1">
                  {{ item.overdueDays }} วัน
                </div>
              </td>
              <td class="text-end">
                <div class="fw-semibold text-danger">
                  {{ formatCurrency(item.totalOutstanding) }}
                </div>
                <div class="small text-muted">
                  ค่าปรับ {{ formatCurrency(item.penaltyAmount) }}
                </div>
              </td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="contactBadgeClass(item.contactStatus)"
                >
                  {{ contactText(item.contactStatus) }}
                </span>
                <div class="small text-muted mt-1" v-if="item.lastReminderDate">
                  เตือนล่าสุด {{ formatDate(item.lastReminderDate) }}
                </div>
              </td>
              <td class="text-center pe-4">
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="ดูรายละเอียด"
                    @click="$emit('view', item)"
                  >
                    <i class="bi bi-eye text-primary-emphasis"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="ติดตาม"
                    @click="$emit('follow-up', item)"
                  >
                    <i class="bi bi-bell-fill text-warning"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="9" class="text-center py-5">
                <div class="text-muted">
                  <i class="bi bi-exclamation-diamond fs-2 d-block mb-2"></i>
                  ไม่พบข้อมูลค้างชำระตามเงื่อนไขที่ค้นหา
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  white-space: nowrap;
  font-size: 0.9rem;
}
</style>
